"use client";
import Link from "next/link";

import { Button } from "@/components/atoms";
import Form from "@/components/molecules/Form";
import { useLogin } from "@/lib/hooks/useSelection";

import { FormCheckbox } from "@/components/molecules/Form/FormCheckbox";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import styles from "../guest.module.css";

const LogInForm = () => {
  const { login: loginFunction } = useLogin();
  const loginErrored = loginFunction.error;

  const loginSchema = z.object({
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email({ message: "Please provide a valid email" }),
    password: z.string().min(8, { message: "Password is required" }),
  });

  const methods = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = methods;

  const onSubmit: SubmitHandler<z.infer<typeof loginSchema>> = async (data) => {
    loginFunction.mutate({
      email: data.email,
      password: data.password,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputs}>
          <Form.TextInputField
            label="Email"
            {...register("email")}
            error={errors.email?.message}
            required
          />
          <Form.TextInputField
            label="Password"
            type="password"
            {...register("password")}
            error={errors.password?.message}
            required
          />
        </div>

        <Button
          color="black"
          grow
          textColor="white"
          className="uppercase"
          style={{ width: "100%", paddingBlock: "1rem" }}
          shadow
        >
          Sign in
        </Button>
        {loginErrored && (
          <p className={styles.error}>
            Couldn&apos;t find username or password
          </p>
        )}

        <div className={styles.options}>
          <FormCheckbox name="rememberMe" label="Remember me" />
          <Link className="underline-hover" href="/glemt-passord">
            Forgotten password?
          </Link>
        </div>
      </form>
    </FormProvider>
  );
};

const LogIn = () => {
  return (
    <div className={styles.page} data-layout="page">
      <section className={styles.section}>
        <h1>Log in to your account</h1>
        <LogInForm />
        <div className={styles.divider} aria-hidden />
        <h2>Not registred? </h2>

        <Link href={"/registrer"}>
          <Button
            color="transparent"
            outlined
            grow
            className="uppercase"
            style={{ width: "100%", paddingBlock: "1rem" }}
          >
            Create an account
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default LogIn;
